"use client";

import { useRef, type ReactNode } from "react";
import { motion, MotionConfig, useInView, useReducedMotion, type Variants } from "framer-motion";
import { cn } from "@/lib/utils";
import { geist } from "@/helpers/geist";

/**
 * Shared plumbing for the home-page product mock-ups (trading journal, daily diary): one
 * scroll-triggered choreography made of opacity / transform tweens, then a still frame.
 *
 * Sizes inside a scene are Figma px ÷ 8 in rem, so the mock-ups scale with the site's fluid root
 * font-size and line up with the 180rem-wide layout.
 */

export const SCENE_EASE = [0.22, 1, 0.36, 1] as const;

export type Cue = { delay: number; x?: number; y?: number; scale?: number; duration?: number };

// Every animated piece shares these two variants and gets its own absolute cue via `custom`,
// which keeps a scene's choreography readable as a list of times.
export const reveal: Variants = {
  hidden: ({ x = 0, y = 0, scale = 1 }: Cue) => ({ opacity: 0, x, y, scale }),
  show: ({ delay, duration = 0.7 }: Cue) => ({
    opacity: 1,
    x: 0,
    y: 0,
    scale: 1,
    transition: { delay, duration, ease: SCENE_EASE },
  }),
};

export function Reveal({
  cue,
  className,
  children,
  as = "div",
}: {
  cue: Cue;
  className?: string;
  children?: ReactNode;
  as?: "div" | "span" | "li";
}) {
  const props = { variants: reveal, custom: cue, className };
  if (as === "li") return <motion.li {...props}>{children}</motion.li>;
  if (as === "span") return <motion.span {...props}>{children}</motion.span>;
  return <motion.div {...props}>{children}</motion.div>;
}

/**
 * Scene root: starts the choreography once the scene scrolls into view and exposes `play` to
 * children; `data-play` is true only while on screen so CSS idle loops can pause off screen.
 * `MotionConfig reducedMotion="user"` keeps server and client markup identical (always starts
 * hidden) while framer drops the transform part of every tween for users who ask for it.
 */
export function SceneRoot({
  label,
  sceneClassName,
  className,
  children,
}: {
  /** Accessible name: the whole mock-up is one `role="img"`. */
  label: string;
  /** The scene's SCSS module root class (tokens + idle loops). */
  sceneClassName: string;
  className?: string;
  children: (play: boolean) => ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const entered = useInView(ref, { once: true, margin: "-50px" });
  const visible = useInView(ref, { amount: 0.15 });
  const play = entered || !!reduced;

  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        ref={ref}
        role="img"
        aria-label={label}
        data-play={play && visible}
        initial="hidden"
        animate={play ? "show" : "hidden"}
        className={cn(sceneClassName, geist.variable, "absolute inset-0 select-none", className)}>
        {children(play)}
      </motion.div>
    </MotionConfig>
  );
}
